/**
 * @file toggle.component.tsx
 * @path packages/ui/src/toggle.component.tsx
 * @description
 */

import { cn } from "@sirbastians/utils";
import * as React from "react";
import * as TogglePrimitive from "@radix-ui/react-toggle";
import { cva, type VariantProps } from "class-variance-authority";

const toggleVariants = cva(
  "inline-flex items-center justify-center rounded-xl text-sm font-medium ring-offset-background transition-all duration-200 hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        // DEFAULT:
        // Off: Transparent (Ghost)
        // Hover: Subtle well
        // On: Hard Bezel Pop (Green)
        default:
          "bg-transparent hover:bg-muted/50 data-[state=on]:neuro-bezel data-[state=on]:bg-accent data-[state=on]:text-accent-foreground",

        // OUTLINE:
        // Off: Deep Well (Recessed)
        // On: Hard Bezel Pop (Green)
        outline:
          "neuro-well bg-secondary border border-input hover:text-accent-foreground data-[state=on]:neuro-bezel data-[state=on]:bg-accent data-[state=on]:text-accent-foreground",
      },
      size: {
        default: "h-11 px-4 min-w-[3rem]",
        sm: "h-9 px-3 min-w-[2.5rem]",
        lg: "h-12 px-6 min-w-[4rem]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const Toggle = React.forwardRef<
  React.ElementRef<typeof TogglePrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TogglePrimitive.Root> &
    VariantProps<typeof toggleVariants>
>(({ className, variant, size, ...props }, ref) => (
  <TogglePrimitive.Root
    ref={ref}
    className={cn(toggleVariants({ variant, size, className }))}
    {...props}
  />
));

Toggle.displayName = TogglePrimitive.Root.displayName;

export { Toggle, toggleVariants };
